import type { FC, ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import ArrowRight from '@wix/da-ds/pkg/Icons/24x24/ArrowRight';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import DollarSignIcon from '@wix/da-ds/pkg/Icons/16x16/DollarSign';
import PointsIcon from '@wix/da-ds/pkg/Icons/16x16/Points';
import Flex from '@wix/da-ds/pkg/Flex';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useDispatch, useSelector } from 'react-redux';
import { pushConfirmationModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import { getIsLoading } from '@wix/da-shared-react/pkg/simpleXHR/selectors';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { PapiRequestStudioEarningsWithdraw } from '@wix/da-papi-types';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';

import s from './WithdrawAsPointsModal.scss';

export interface Props {
  isOpen: boolean;
  requestClose: () => void;
  availableBalance: number;
  onConvert?: (amountUsd: number, points: number) => void;
}

const POINTS_PER_USD = 80;

const WithdrawAsPointsModal: FC<Props> = ({
  isOpen,
  requestClose,
  availableBalance,
  onConvert,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logEvent = useBiLogger();
  const isLoading = useSelector((state: any) =>
    getIsLoading(state, SimpleXHREndpoint.WITHDRAW_EARNINGS)
  );
  const [amountStr, setAmountStr] = useState<string>('');
  const [pointsStr, setPointsStr] = useState<string>('');

  const amountNumber = (() => {
    const parsed = parseFloat(amountStr);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, Math.min(parsed, availableBalance));
  })();

  const pointsNumber = (() => {
    const parsed = parseInt(pointsStr, 10);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, parsed);
  })();

  const isAmountTooHigh = (() => {
    const parsed = parseFloat(amountStr);
    return !Number.isNaN(parsed) && parsed > availableBalance;
  })();

  const handleAmountChange = (ev: ChangeEvent<HTMLInputElement>) => {
    // Normalize comma to dot first, then allow only digits and a dot
    const normalized = ev.target.value.replace(/,/g, '.');
    const sanitized = normalized.replace(/[^0-9.]/g, '');
    setAmountStr(sanitized);
    // Update points when amount changes
    const parsed = parseFloat(sanitized);
    if (!Number.isNaN(parsed)) {
      setPointsStr(Math.floor(parsed * POINTS_PER_USD).toString());
    } else {
      setPointsStr('');
    }
  };

  const handlePointsChange = (ev: ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers
    const sanitized = ev.target.value.replace(/[^0-9]/g, '');
    setPointsStr(sanitized);
    // Update amount when points change
    const parsed = parseInt(sanitized, 10);
    if (!Number.isNaN(parsed)) {
      const dollarAmount = parsed / POINTS_PER_USD;
      // Ensure dot as decimal separator regardless of locale
      const formatted = dollarAmount.toFixed(2);
      setAmountStr(formatted.replace(',', '.'));
    } else {
      setAmountStr('');
    }
  };

  const handleConvert = () => {
    logEvent(
      PureBiData<StudioEarningsWithdrawalClickBiEvent>({
        evid: 159,
        payment_method: StudioWithdrawalPaymentMethod.Points,
        process_step: 'withdraw',
      })
    );
    dispatch(
      makeXHRCall({
        papiBase: 'studio',
        endpoint: SimpleXHREndpoint.WITHDRAW_EARNINGS,
        method: SimpleXHRMethod.POST,
        data: {
          method: 'points',
          amount: amountNumber.toString(),
        } as PapiRequestStudioEarningsWithdraw,
        onSuccess: () => {
          onConvert?.(amountNumber, pointsNumber);
          requestClose();
          dispatch(
            pushConfirmationModal({
              title: t('studio.earnings.withdrawAsPoints.title'),
              text: t('studio.earnings.withdrawAsPoints.successMessage'),
              confirmBtnLabel: t('common.gotIt'),
              confirmBtnType: 'default',
              withCancelButton: false,
              onConfirm: () => {
                window.location.reload();
              },
              onCancel: () => {
                window.location.reload();
              },
            })
          );
        },
        onError: (response: any) => {
          requestClose();
          dispatch(
            pushConfirmationModal({
              title: t('studio.earnings.withdrawAsPoints.error.title'),
              text:
                response?.errorDescription ||
                t('studio.earnings.withdrawAsPoints.error.message'),
              confirmBtnLabel: t('common.gotIt'),
              withCancelButton: false,
              confirmBtnType: 'default',
            })
          );
        },
      })
    );
  };

  return (
    <Modal
      className={s['wrapper']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      heading={t('studio.earnings.withdrawAsPoints.title')}
      onRequestClose={requestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          onClick={requestClose}
          size="large"
          variant="main"
          key="cancel"
        >
          {t('common.cancel')}
        </TextButton>,
        <BrandedButton
          onClick={handleConvert}
          size="large"
          variant="brand-green"
          key="convert"
          disabled={
            amountNumber <= 0 ||
            pointsNumber <= 0 ||
            isAmountTooHigh ||
            isLoading
          }
          loading={isLoading}
        >
          {t('studio.earnings.withdrawal.convertToPoints')}
        </BrandedButton>,
      ]}
    >
      <div className={s['subtitle']}>
        {t('studio.earnings.withdrawAsPoints.subtitle')}
      </div>

      <div className={s['note']}>
        {t('studio.earnings.withdrawAsPoints.note')}
      </div>

      <Flex
        direction="row"
        alignItems="center"
        gap={8}
        className={s['converter-row']}
      >
        <Flex direction="column" flex={1}>
          <TextInput
            autoFocus
            id="withdraw-amount"
            label={t('studio.earnings.withdrawAsPoints.amountToWithdraw')}
            value={amountStr}
            onChange={handleAmountChange}
            placeholder="0.00"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            wrapperClassName={s['input']}
            validationResult={isAmountTooHigh ? 'error' : undefined}
            prefix={
              <DollarSignIcon
                size={IconSize.SMALLER}
                className={s['icon']}
                disableDefaultColors
              />
            }
            details={t('studio.earnings.withdrawAsPoints.available', {
              amount: formatDollars(availableBalance),
            })}
          />
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="center"
          className={s['arrow']}
        >
          <ArrowRight />
        </Flex>

        <Flex direction="column" flex={1}>
          <TextInput
            id="withdraw-points"
            label={t('studio.earnings.withdrawAsPoints.points')}
            value={pointsStr}
            onChange={handlePointsChange}
            placeholder="0"
            type="number"
            step="1"
            wrapperClassName={s['input']}
            prefix={
              <PointsIcon
                size={IconSize.SMALLER}
                className={s['icon']}
                disableDefaultColors
              />
            }
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

WithdrawAsPointsModal.displayName = 'WithdrawAsPointsModal';

export default WithdrawAsPointsModal;
